import { siteService } from '@/services/site.service';
import { LicenseeTypeEnums } from '@nf/types/site';
import { getAuthToken, getAuthActions } from '@/store/auth';
import { getResponseJwtHeader } from '@/utils/web';
import { fetchComposer } from '@nf/utils-common/compose-fetch';

type DepositReloginResponse = {
	Data: { Url: string };
	Success: boolean;
	Code: string;
	Message: string;
};

export type DepositReloginResult =
	| { success: true; url: string }
	| { success: false; message: string };

export async function depositRelogin(ctcdDomain: string, language: string): Promise<DepositReloginResult> {
	const path = siteService.getLicenseeType() === LicenseeTypeEnums.Api ? 'ApiSiteLogin' : 'AthenaLogin';
	const url = `${ctcdDomain}/api/${path}/Relogin`;
	const authToken = getAuthToken();

	try {
		const response = await fetchComposer.postWithBearer(url, { body: JSON.stringify({ language }) })(authToken);

		const refreshedToken = getResponseJwtHeader(response);
		if (refreshedToken) {
			getAuthActions().updateAuthToken(refreshedToken);
		}

		// Use text() first so empty or non-JSON bodies produce actionable error messages.
		const bodyText = await response.text();

		if (!response.ok) {
			return {
				success: false,
				message: `HTTP ${response.status} ${response.statusText} (url=${url}): ${bodyText.slice(0, 300)}`
			};
		}

		if (!bodyText) {
			return {
				success: false,
				message: `Empty response body (status=${response.status}, content-type=${response.headers.get('content-type')}, url=${url})`
			};
		}

		let data: DepositReloginResponse;
		try {
			data = JSON.parse(bodyText);
		} catch {
			return {
				success: false,
				message: `Non-JSON response (status=${response.status}, url=${url}): ${bodyText.slice(0, 300)}`
			};
		}

		if (!data.Success || !data.Data?.Url) {
			return { success: false, message: `${data.Code}: ${data.Message}` };
		}

		return { success: true, url: data.Data.Url };
	} catch (error: any) {
		const message = error instanceof Error
			? `${error.message} (url=${url})`
			: `${error?.status}: ${error?.statusText} (url=${url})`;

		return { success: false, message };
	}
}
